import React, { useEffect, useState } from 'react';

import type { CityRef } from '../types';
import { ActionDialog } from './dialogs';
import { FormField, TextInput } from './form';
import { CitySelect } from './pickers';

interface ReadyToPickupModalProps {
  open: boolean;
  /** Goods issue code (subtitle). */
  code: string;
  /** Current picker — pre-fills the field. */
  initialPicker: string | null;
  /** The outbound order has no destination city yet: ask for it here, in
   *  the same request (the server refuses Ready to pick up without one). */
  askCity?: boolean;
  busy?: boolean;
  /** Server validation message (picker or city). */
  error?: string | null;
  onClose: () => void;
  onConfirm: (pickerName: string, destinationCityID: number | null) => void;
}

/** Ready to pick up: confirm with the picker (and the destination city when
 *  the order still lacks one) — both set in the same request. */
const ReadyToPickupModal: React.FC<ReadyToPickupModalProps> = ({
  open,
  code,
  initialPicker,
  askCity = false,
  busy,
  error,
  onClose,
  onConfirm,
}) => {
  const [name, setName] = useState('');
  const [city, setCity] = useState<CityRef | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialPicker ?? '');
      setCity(null);
    }
  }, [open, initialPicker]);

  const value = name.trim();
  const cityOk = !askCity || city !== null;

  return (
    <ActionDialog
      open={open}
      title="Ready to pick up"
      subtitle={code}
      description="Setelah ready, picking list tidak bisa diubah."
      confirmLabel="Ready to pick up"
      busy={busy}
      confirmDisabled={!value || !cityOk}
      disabledReason={!value ? 'Picker wajib diisi' : 'Kota tujuan wajib dipilih'}
      testId="picking-ready-dialog"
      onConfirm={() => onConfirm(value, city?.id ?? null)}
      onClose={onClose}
    >
      <FormField
        label="Picker"
        htmlFor="gi-ready-picker"
        required
        error={error && !askCity ? error : null}
      >
        <TextInput
          id="gi-ready-picker"
          size="md"
          value={name}
          onChange={setName}
          placeholder="Nama picker"
          invalid={!!error && !askCity}
          disabled={busy}
        />
      </FormField>
      {askCity && (
        <FormField
          label="Kota tujuan"
          htmlFor="gi-ready-city"
          required
          hint="Outbound request ini belum punya kota (tidak dikenali dari alamat) — dipakai export & filter."
          error={error ?? null}
          testId="picking-ready-city"
        >
          <CitySelect
            value={city}
            onChange={(c) => setCity(c)}
            disabled={busy}
            invalid={!!error}
            placeholder="Pilih kota (master core-service)"
            aria-label="Kota tujuan"
          />
        </FormField>
      )}
    </ActionDialog>
  );
};

export default ReadyToPickupModal;
